import { Icon, MenuButton, shortAge, type IconName } from '@hive/ui';
import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { useI18nP } from '../i18n';
import { useSession } from './session';

/**
 * Peças comuns da varredura 3 do layout A + C (alertas, relatórios, rede, mapas, NOC/SOC, Início e telas de
 * experiência): o "⋯" do cabeçalho (o que saiu do cabeçalho enxuto) e a idade dos dados ao lado do título.
 */

export interface MoreItem {
  id: string;
  label: ReactNode;
  /** Link (rota da console) ou ação. */
  to?: string;
  onClick?: () => void;
  /** Permissão exigida (sem ela o item não aparece). */
  perm?: string;
  icon?: IconName;
  hidden?: boolean;
}

/** "⋯" do cabeçalho: um menu com as ações e atalhos que não são a ação principal da tela. Some sem itens. */
export function MoreMenu({ items }: { items: MoreItem[] }) {
  const { t } = useI18nP();
  const s = useSession();
  const list = items.filter((x) => !x.hidden && (!x.perm || s.can(x.perm)));
  if (!list.length) return null;
  return (
    <MenuButton className="btn" label={t('sw3.more')} menuLabel={t('sw3.more')} button={<span aria-hidden="true">⋯</span>}>
      {(close) => (
        <div className="cols-pop">
          {list.map((x) =>
            x.to ? (
              <Link key={x.id} role="menuitem" className="link-btn pop-item small" to={x.to} onClick={() => close()}>
                {x.icon && <Icon name={x.icon} size="sm" />} {x.label}
              </Link>
            ) : (
              <button
                key={x.id}
                type="button"
                role="menuitem"
                className="link-btn pop-item small"
                style={{ textAlign: 'left' }}
                onClick={() => {
                  close();
                  x.onClick?.();
                }}
              >
                {x.icon && <Icon name={x.icon} size="sm" />} {x.label}
              </button>
            ),
          )}
        </div>
      )}
    </MenuButton>
  );
}

/**
 * Idade dos dados no cabeçalho ("dados há 18 s"), atualizada sozinha. Enquanto não carregou, guarda o lugar com o
 * mesmo texto invisível: o texto chegando depois alargava as ações do cabeçalho, que quebravam para a linha de baixo e
 * empurravam a tela inteira (CLS de /alerts, QA 5 · Q5-6).
 */
export function DataAge({ at, what }: { at: number | string | null | undefined; what?: string }) {
  const { t } = useI18nP();
  const ms = at == null ? null : new Date(at).getTime();
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (ms == null) return;
    const id = window.setInterval(() => setNow(Date.now()), 15_000);
    return () => window.clearInterval(id);
  }, [ms]);
  if (ms == null || Number.isNaN(ms)) {
    const age = shortAge(0);
    return (
      <span className="xs muted sw3-age" aria-hidden="true" style={{ visibility: 'hidden' }}>
        {what ? t('ui.status.updatedWhat', { what, age }) : t('ui.status.updated', { age })}
      </span>
    );
  }
  const age = shortAge(Math.max(0, now - ms));
  return (
    <span className="xs muted sw3-age" data-testid="data-age">
      {what ? t('ui.status.updatedWhat', { what, age }) : t('ui.status.updated', { age })}
    </span>
  );
}

/** Momento em que um dado carregou (para a idade no cabeçalho e no rodapé). */
export function useLoadedAt(data: unknown): number | null {
  const [at, setAt] = useState<number | null>(null);
  useEffect(() => {
    if (data != null) setAt(Date.now());
  }, [data]);
  return at;
}

/** Busca simples: todos os textos do item contêm o termo (sem acento e sem caixa). */
export function matchText(needle: string, ...parts: Array<string | number | null | undefined>): boolean {
  const n = fold(needle.trim());
  if (!n) return true;
  return fold(parts.filter((x) => x != null).join(' ')).includes(n);
}
const fold = (s: string) => s.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase();

type Box = { x: number; y: number; w: number; h: number };

/**
 * Mapa no padrão da Nuvem (`screens/cloud/CloudMapView.tsx`) para desenhos SVG de tamanho conhecido: abre ajustado ao
 * quadro (o desenho inteiro no viewBox, nada cortado), −/+/Ajustar, arrastar, Ctrl + roda, tela cheia (Esc fecha),
 * legenda recolhida e, na barra, o que a tela quiser (camadas, Exportar). O detalhe do nó é a gaveta da tela.
 */
export function ViewBoxMap({
  title,
  width,
  height,
  children,
  legend,
  tools,
  svgRef,
}: {
  title: string;
  width: number;
  height: number;
  children: ReactNode;
  legend?: ReactNode;
  tools?: ReactNode;
  svgRef?: (el: SVGSVGElement | null) => void;
}) {
  const { t } = useI18nP();
  const fit: Box = { x: 0, y: 0, w: Math.max(1, width), h: Math.max(1, height) };
  const [view, setView] = useState<Box>(fit);
  const [full, setFull] = useState(false);
  const [drag, setDrag] = useState<{ x: number; y: number; v: Box } | null>(null);
  // desenho novo (outro tamanho): volta a ajustar
  useEffect(() => setView({ x: 0, y: 0, w: Math.max(1, width), h: Math.max(1, height) }), [width, height]);
  useEffect(() => {
    if (!full) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !document.querySelector('.scrim-modal, .detail-panel')) setFull(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [full]);
  const zoom = (f: number) =>
    setView((v) => {
      const w = Math.min(fit.w * 4, Math.max(60, v.w * f));
      const h = (w / v.w) * v.h;
      return { x: v.x + (v.w - w) / 2, y: v.y + (v.h - h) / 2, w, h };
    });
  return (
    <section className={full ? 'cl-map full' : 'cl-map'} aria-label={title}>
      <div className="cl-map-bar">
        {full && <b className="cl-map-title">{title}</b>}
        {tools}
        <span style={{ flex: 1 }} />
        <span className="cl-map-zoom" role="group" aria-label={t('sw3.map.zoom')}>
          <button type="button" className="icon-btn" aria-label={t('ui.map.zoomOut')} title={t('ui.map.zoomOut')} onClick={() => zoom(1.25)}>
            −
          </button>
          <button type="button" className="icon-btn" aria-label={t('ui.map.zoomIn')} title={t('ui.map.zoomIn')} onClick={() => zoom(0.8)}>
            +
          </button>
          <button type="button" className="btn btn-sm" onClick={() => setView(fit)}>
            {t('ui.map.fit')}
          </button>
        </span>
        <button type="button" className="btn btn-sm" aria-pressed={full} onClick={() => setFull(!full)}>
          <Icon name={full ? 'x' : 'grid'} size="sm" /> {full ? t('map.close') : t('map.fullscreen')}
        </button>
      </div>
      <div className="cl-map-canvas">
        <svg
          ref={svgRef}
          width="100%"
          height="100%"
          viewBox={`${view.x} ${view.y} ${view.w} ${view.h}`}
          preserveAspectRatio="xMidYMid meet"
          role="img"
          aria-label={title}
          style={{ display: 'block', touchAction: 'none', cursor: drag ? 'grabbing' : 'grab' }}
          onPointerDown={(e) => {
            if ((e.target as Element).closest('[role="button"]')) return;
            setDrag({ x: e.clientX, y: e.clientY, v: view });
            (e.currentTarget as Element).setPointerCapture?.(e.pointerId);
          }}
          onPointerMove={(e) => {
            const el = e.currentTarget;
            if (!drag || !el.clientWidth) return;
            const k = drag.v.w / el.clientWidth;
            setView({ ...drag.v, x: drag.v.x - (e.clientX - drag.x) * k, y: drag.v.y - (e.clientY - drag.y) * k });
          }}
          onPointerUp={() => setDrag(null)}
          onPointerLeave={() => setDrag(null)}
          onWheel={(e) => {
            if (!e.ctrlKey && !e.metaKey) return;
            e.preventDefault();
            zoom(e.deltaY > 0 ? 1.15 : 0.87);
          }}
        >
          {children}
        </svg>
      </div>
      {legend && (
        <details className="cl-map-legend">
          <summary>{t('sw3.map.legend')}</summary>
          <div className="xs muted">{legend}</div>
        </details>
      )}
    </section>
  );
}

/** Corta o rótulo para caber (o nome inteiro vai na dica `<title>`), como no mapa da Nuvem. */
export function cutLabel(label: string, max: number): string {
  return label.length <= max ? label : `${label.slice(0, Math.max(1, max - 1))}…`;
}

/** Passa nas facetas: cada grupo marcado exige que o valor do item esteja entre os marcados. */
export function facetOk(value: Record<string, string[]>, get: (group: string) => string | string[] | null | undefined, skip?: string): boolean {
  return Object.entries(value).every(([g, vs]) => {
    if (g === skip || !vs?.length) return true;
    const v = get(g);
    if (v == null) return false;
    return Array.isArray(v) ? v.some((x) => vs.includes(x)) : vs.includes(v);
  });
}
